import { KeyboardEvent } from 'react';
import { Folder, FileIcon, Copy, Check } from 'lucide-react';

import { useCopyToClipboard } from '@app/hooks/useCopyToClipboard';
import { FileEntry } from '@app/api/files/types';

import { LoadingIndicator } from './LoadingIndicator';

interface FileCardProps {
  contents: FileEntry[];
  currentPath: string;
  isLoading: boolean;
  isDirectoryBrowsable: (path: string) => boolean;
  navigateTo: (path: string) => void;
  getParentDirectory: (path: string) => string;
  className?: string;
}

export function FileCard({
  contents,
  currentPath,
  isLoading,
  isDirectoryBrowsable,
  navigateTo,
  getParentDirectory,
  className = '',
}: FileCardProps) {
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // Create data URL from path
  const getDataUrl = (path: string) => {
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    return `https://data.jua.ai${formattedPath}`;
  };

  // Handle copying the data URL to clipboard
  const handleCopy = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = getDataUrl(path);
    copyToClipboard(url, path);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent, callback: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  // Handle keyboard navigation for copy button
  const handleCopyKeyDown = (e: KeyboardEvent, path: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      const url = getDataUrl(path);
      copyToClipboard(url, path);
    }
  };

  return (
    <div className={`min-h-[400px] relative ${className}`}>
      {/* Loading overlay */}
      {isLoading && <LoadingIndicator fullscreen message="Loading..." />}

      <div className="space-y-2 p-4">
        {/* Parent directory entry (if not at root) */}
        {!isLoading && currentPath !== '/' && (
          <div
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E34290] transition-colors duration-200"
            onClick={() => navigateTo(getParentDirectory(currentPath))}
            onKeyDown={(e) => handleKeyDown(e, () => navigateTo(getParentDirectory(currentPath)))}
            tabIndex={0}
            role="button"
            aria-label="Parent directory"
          >
            <div className="flex items-center">
              <Folder className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">..</span>
            </div>
            <span className="text-gray-400 dark:text-gray-500 text-sm">-</span>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && contents.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>This directory is empty</p>
          </div>
        )}

        {/* Directory and file entries */}
        {!isLoading &&
          contents.map((entry) => {
            // Determine if this directory is clickable
            const isZarrSubdirectory =
              entry.type === 'directory' && !isDirectoryBrowsable(entry.path);
            const isClickable = entry.type === 'directory' && !isZarrSubdirectory;

            return (
              <div
                key={entry.path}
                className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors duration-200 ${
                  isClickable
                    ? 'hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E34290]'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                onClick={isClickable ? () => navigateTo(entry.path) : undefined}
                onKeyDown={
                  isClickable ? (e) => handleKeyDown(e, () => navigateTo(entry.path)) : undefined
                }
                tabIndex={isClickable ? 0 : undefined}
                role={isClickable ? 'button' : undefined}
                aria-label={isClickable ? `Navigate to ${entry.name}` : undefined}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {entry.type === 'directory' ? (
                    <Folder
                      className={`flex-shrink-0 h-5 w-5 mr-3 ${
                        isZarrSubdirectory
                          ? 'text-gray-400 dark:text-gray-500'
                          : 'text-[#E34290] dark:text-[#E34290]'
                      }`}
                    />
                  ) : (
                    <FileIcon className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400 mr-3" />
                  )}
                  <span
                    className={`text-sm font-medium truncate ${
                      isZarrSubdirectory
                        ? 'text-gray-500 dark:text-gray-400'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {entry.name}
                  </span>
                </div>

                <button
                  onClick={(e) => handleCopy(entry.path, e)}
                  onKeyDown={(e) => handleCopyKeyDown(e, entry.path)}
                  className="ml-2 p-2 text-[#E34290] hover:text-[#E34290]/80 dark:text-[#E34290] dark:hover:text-[#E34290]/80 focus:outline-none focus:ring-2 focus:ring-[#E34290] rounded-md transition-colors duration-200"
                  aria-label={`Copy URL for ${entry.name}`}
                  data-ph-capture-attribute="btn-file-card-copy"
                >
                  {isCopied(entry.path) ? (
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            );
          })}
      </div>
    </div>
  );
}
